import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import API_BASE_URL from '../config/api';

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t('auth.verifyMissingLink'));
      return;
    }

    fetch(`${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setStatus('success');
          setMessage(data.message || t('auth.verifySuccess'));
        } else {
          setStatus('error');
          setMessage(data.detail || t('auth.verifyInvalidLink'));
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage(t('auth.verifyConnectionError'));
      });
  }, [searchParams, t]);

  return (
    <div className="min-h-screen flex flex-col bg-namke-white">
      <NavBar />
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200/90 p-10 text-center">
          {status === 'loading' && (
            <>
              <div className="w-12 h-12 border-4 border-gray-200 border-t-primary-700 rounded-full animate-spin mx-auto mb-6" />
              <p className="text-gray-600">{t('auth.verifyingInProgress')}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="rgb(5,70,37)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h2 className="title-font text-2xl font-bold text-primary-700 mb-3">{t('auth.emailVerified')}</h2>
              <p className="text-gray-600 mb-8">{message}</p>
              <Link
                to="/login"
                className="inline-block bg-primary-700 text-white py-3 px-8 rounded-xl hover:bg-primary-800 transition-colors font-semibold"
              >
                {t('auth.login')}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
              </div>
              <h2 className="title-font text-2xl font-bold text-gray-800 mb-3">{t('auth.invalidLink')}</h2>
              <p className="text-gray-600 mb-8">{message}</p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/login"
                  className="inline-block bg-primary-700 text-white py-3 px-8 rounded-xl hover:bg-primary-800 transition-colors font-semibold"
                >
                  {t('auth.backToLogin2')}
                </Link>
                <Link
                  to="/signup"
                  className="text-sm text-primary-700 hover:text-primary-800 font-medium"
                >
                  {t('auth.createAccountLink')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
